import { API_ENDPOINTS, fetchWithTimeout } from '../config/api';

// In-flight dedup + short-lived cache: concurrent callers for the same ID
// share one request; the result is reused for 30s before re-fetching.
const _detailCache = new Map(); // productId -> { promise, ts }
const _DETAIL_TTL = 30_000;

export const productService = {
    /**
     * Search products with filters and pagination
     * @param {Object} params - Query parameters
     * @param {string} [params.q] - Search query
     * @param {string} [params.category] - Category filter
     * @param {number} [params.min_price] - Min price
     * @param {number} [params.max_price] - Max price
     * @param {string} [params.location] - Location filter
     * @param {string} [params.cursor] - Pagination cursor
     * @param {number} [params.limit] - Items per page
     * @returns {Promise<{items: Array, next_cursor: string}>}
     */
    searchProducts: async (params = {}) => {
        try {
            // Filter out empty/null values
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    queryParams.append(key, value);
                }
            });

            const response = await fetch(`${API_ENDPOINTS.PRODUCTS.SEARCH}?${queryParams.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch products');
            }

            const data = await response.json();
            console.log('Fetched products:', data);
            return data; // Expected format: { ok: true, items: [], next_cursor: "..." }
        } catch (error) {
            console.error('Error fetching products:', error);
            throw error;
        }
    },

    /**
     * Get product details by ID
     * @param {string} productId 
     */
    /**
     * Offer of the Day / New Arrivals carousel — active seller promotions.
     * @param {Object} params
     * @param {number} [params.limit=12]
     * @param {string} [params.city]
     * @param {string} [params.sort=latest_offer] latest_offer | best_offer | newest_listing
     */
    getNewArrivals: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== "") {
                    queryParams.append(key, value);
                }
            });

            const response = await fetchWithTimeout(
                `${API_ENDPOINTS.PRODUCTS.NEW_ARRIVALS}?${queryParams.toString()}`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch offer of the day");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching new arrivals:", error);
            throw error;
        }
    },

    getProductDetails: (productId) => {
        const now = Date.now();
        const cached = _detailCache.get(productId);
        if (cached && now - cached.ts < _DETAIL_TTL) return cached.promise;

        const promise = fetch(`${API_ENDPOINTS.PRODUCTS.SEARCH}/${productId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
            .then(res => {
                if (!res.ok) {
                    const error = new Error('Failed to fetch product details');
                    error.status = res.status;
                    throw error;
                }
                return res.json();
            })
            .catch(err => {
                _detailCache.delete(productId); // don't cache errors
                if (err.status !== 404) console.error('Error fetching product details:', err);
                throw err;
            });

        _detailCache.set(productId, { promise, ts: now });
        return promise;
    }
};

/** Call this after updating a product (e.g. status change) to force a fresh fetch */
export const clearProductCache = (productId) => {
    if (productId) _detailCache.delete(productId);
};

export const viewProduct = async (product_id) => {
    try {
        const response = await fetch(`${API_ENDPOINTS.PRODUCTS.VIEW}/${product_id}/view`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            console.warn('View tracking returned non-ok status:', response.status);
            return null;
        }
        const data = await response.json();
        return data;
    } catch (error) {
        // Non-critical: just log, don't throw
        console.warn('View tracking failed (non-critical):', error.message);
        return null;
    }
};
